import type { Folder, Scroll } from "@/lib/supabase";

export type FolderArchiveFields = Pick<
  Folder,
  "id" | "is_archived" | "is_deleted"
>;

export type ScrollHierarchyFields = Pick<
  Scroll,
  "is_archived" | "is_deleted" | "folder_id"
> & {
  trashed_by_folder_id?: string | null;
};

/**
 * Map folder id → folder row for O(1) parent lookups when evaluating scroll archive state.
 */
export function buildFolderArchiveMap(
  folders: FolderArchiveFields[],
): Map<string, FolderArchiveFields> {
  const m = new Map<string, FolderArchiveFields>();
  for (const f of folders) {
    m.set(f.id, f);
  }
  return m;
}

export function isFolderTrashedRow(
  folder: FolderArchiveFields | null | undefined,
): boolean {
  return !!(folder && folder.is_deleted);
}

export function isFolderArchivedRow(
  folder: FolderArchiveFields | null | undefined,
): boolean {
  return !!(folder && !folder.is_deleted && folder.is_archived);
}

export function isScrollInTrashedFolder(
  scroll: ScrollHierarchyFields,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (scroll.trashed_by_folder_id != null) return true;
  if (!scroll.folder_id) return false;
  return isFolderTrashedRow(folderById.get(scroll.folder_id));
}

/**
 * A scroll is “archived” for UX/storage if the note flag is set OR its folder is archived.
 * Does not treat trash (`is_deleted`) as archived — trash is a separate lifecycle.
 */
export function isScrollArchivedIncludingParent(
  scroll: Pick<Scroll, "is_archived" | "is_deleted" | "folder_id">,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (scroll.is_deleted) return false;
  if (scroll.is_archived) return true;
  if (!scroll.folder_id) return false;
  return isFolderArchivedRow(folderById.get(scroll.folder_id));
}

/**
 * Visible on workspace home: not deleted, not archived by note, not inside an archived folder.
 */
export function isScrollLiveOnWorkspaceHome(
  scroll: ScrollHierarchyFields,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (scroll.is_deleted) return false;
  if (isScrollInTrashedFolder(scroll, folderById)) return false;
  return !isScrollArchivedIncludingParent(scroll, folderById);
}

/** Block mutations on notes whose parent folder is in trash. */
export function isScrollInteractable(
  scroll: ScrollHierarchyFields,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (scroll.is_deleted) return false;
  if (isScrollInTrashedFolder(scroll, folderById)) return false;
  return true;
}

/**
 * In Archive smart view “Archived notes” flat grid: show explicitly archived notes and orphans,
 * but omit notes that are only archived because their folder is archived (those appear via folder cards).
 */
export function showScrollInArchiveFlatNotesSection(
  scroll: Pick<Scroll, "is_archived" | "is_deleted" | "folder_id">,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (!isScrollArchivedIncludingParent(scroll, folderById)) return false;
  const parent = scroll.folder_id
    ? folderById.get(scroll.folder_id)
    : undefined;
  if (
    isFolderArchivedRow(parent) &&
    !scroll.is_archived
  ) {
    return false;
  }
  return true;
}

/** Hide per-note “Archive” when the folder already archives all contained notes. */
export function shouldOfferArchiveNoteAction(
  scroll: Pick<Scroll, "folder_id">,
  folderById: Map<string, FolderArchiveFields>,
): boolean {
  if (!scroll.folder_id) return true;
  const parent = folderById.get(scroll.folder_id);
  if (isFolderTrashedRow(parent)) return false;
  return !isFolderArchivedRow(parent);
}
